/**
 * Copy for the "Delete this task?" confirmations on the task and sub-task delete
 * buttons in TaskDetailRow.
 *
 * Deleting a task is a SOFT delete — `DELETE /api/project-tasks/:id` stamps
 * `deleted_at` on that one row (server/routes.ts) and the nightly
 * `purgeOldTrash` hard-deletes anything stamped more than 30 days ago
 * (server/lib/trash.ts). So the honest thing to tell someone is that the task is
 * recoverable, not that it is gone.
 *
 * The two subjects differ in a way that matters, and it is the reason these are
 * separate builders rather than one:
 *   - a ROOT task is listed on /trash and can be restored from the UI;
 *   - a SUB-task is NOT — `GET /api/trash?type=tasks` filters
 *     `.is("parent_task_id", null)`, so nothing in the app surfaces it again.
 * Promising a restore that has no button is worse than saying nothing.
 *
 * Deleting a parent does NOT stamp its children, so its sub-tasks simply travel
 * with it (hidden while the parent is deleted, back when it is restored).
 *
 * The sentences returned here render after "<title> will be deleted."; an empty
 * array is possible in principle but neither builder returns one today.
 */

export interface TaskDeleteWarningInput {
  /** Sub-tasks loaded for the row. 0 when the row has never been expanded. */
  subTaskCount: number;
  isClientTask?: boolean;
  /** Client tasks only reach the portal once they've been sent. */
  sentAt?: string | null;
}

export function buildTaskDeleteWarning(input: TaskDeleteWarningInput): string[] {
  const lines: string[] = [];

  const subs = Math.max(0, input?.subTaskCount ?? 0);
  if (subs > 0) {
    const plural = subs === 1 ? "sub-task" : "sub-tasks";
    lines.push(`Its ${subs} ${plural} go with it, and come back if you restore it.`);
  }

  // Only a SENT client task is visible in the portal (v1.2.80 gates the portal
  // task list on sent_at), so an unsent one gets no portal claim.
  if (input?.isClientTask && input?.sentAt) {
    lines.push("This is a client task the client can currently see — it will disappear from their portal.");
  }

  lines.push("It moves to Trash, where you can restore it for 30 days.");
  return lines;
}

export function buildSubTaskDeleteWarning(): string[] {
  return [
    "Trash lists whole tasks only, so deleting a sub-task can't be undone from the app.",
  ];
}
